import React, { ComponentProps, forwardRef, useCallback, useEffect, useState } from 'react';

import { Container, Content, Options } from './RadioSelector.styled';
import { Typography } from '../Typography/Typography';
import { Checkbox } from '../Checkbox/Checkbox';
import Shimmer from '../Shimmer/Shimmer';

type OptionType = {
  label: string;
  value: string;
};

interface RadioSelectorProps extends Omit<ComponentProps<typeof Checkbox>, 'onChange' | 'name'> {
  label?: string;
  orientation?: 'column' | 'row';
  value?: string;
  onChange?: (value: string) => void;
  options: OptionType[];
  loading?: boolean;
  shimmerLength?: number;
  name?: string;
}

const RadioSelector = forwardRef<HTMLInputElement, RadioSelectorProps>(
  ({ label, orientation = 'column', options, loading = false, shimmerLength = 4, value = '', ...props }, ref) => {
    const handleChange = useCallback(
      (checked: boolean, value: string) => {
        props.onChange?.(checked ? value : null);
      },
      [props],
    );

    return (
      <Container>
        <Typography element="label" type="paragraphSmall" weight={600}>
          {label}
        </Typography>
        <Options orientation={orientation}>
          {Array.isArray(options) &&
            !loading &&
            options.map((option) => (
              <Checkbox
                name={option.value}
                key={option.value}
                radio
                checked={option.value === value}
                {...props}
                onChange={(value) => handleChange(value, option.value)}
              >
                {option.label}
              </Checkbox>
            ))}
          {loading &&
            Array.from({ length: shimmerLength }).map((_, index) => (
              <Content key={index}>
                <Checkbox name="loading" radio disabled />
                <Shimmer width={150} height={24} br={8} />
              </Content>
            ))}
        </Options>
      </Container>
    );
  },
);

export default RadioSelector;
